import { useCallback } from "react";
import type { SplitTreatments } from "@zapier/bff";
import { useCurrentAccountId } from "@zapier/identity";
import { trpc, type RouterOutputs } from "../trpc";
import { buildIsAppRestricted } from "../account/useControlledAppAccess";
import { useUserAccountRole } from "../permissions/useUserAccountRole";
import {
  buildIsAppOperationRestricted,
  MANAGED_GUARDS_INPUT,
} from "./useQueryManagedGuards";

export type CreateBlockReason = "restricted" | "managed";

// The backend only enforces managed_auth_only where both are on.
const MANAGED_AUTH_SPLITS = [
  "auth_guards_enabled",
  "auth_guards_managed_auth_only",
] satisfies SplitTreatments[];

type SplitCheckOutput = RouterOutputs["split"]["check"];

function isManagedAuthEnforcedBy(treatments: SplitCheckOutput | undefined) {
  return (
    treatments?.auth_guards_enabled === "on" &&
    treatments?.auth_guards_managed_auth_only === "on"
  );
}

/** Why a create is blocked, or `undefined` when it's allowed. Restricted wins. */
export function resolveCreateBlock({
  isRestricted,
  isManagedCreateRestricted,
  isManaged,
}: {
  isRestricted: boolean;
  isManagedCreateRestricted: boolean;
  isManaged: boolean;
}): CreateBlockReason | undefined {
  if (isRestricted) return "restricted";
  if (!isManaged && isManagedCreateRestricted) return "managed";
  return undefined;
}

/** Whether the backend enforces managed_auth_only guards for this account. */
export function useManagedAuthEnforcement() {
  const accountId = useCurrentAccountId();
  const { data, isLoading } = trpc.split.check.useQuery(
    { accountId, featureFlags: MANAGED_AUTH_SPLITS },
    { staleTime: 1000 * 60 * 10 },
  );

  return { isManagedAuthEnforced: isManagedAuthEnforcedBy(data), isLoading };
}

/** Async check for one create; fetches rules data on demand and allows it if a fetch fails. */
export function useEnsureCreateBlock() {
  const utils = trpc.useUtils();
  const accountId = useCurrentAccountId();
  const { isAccountOwner, isSuperAdmin } = useUserAccountRole();
  const isOwnerOrSuperAdmin = isAccountOwner || isSuperAdmin;

  return useCallback(
    async (
      appId: string,
      { isManaged = false }: { isManaged?: boolean } = {},
    ): Promise<CreateBlockReason | undefined> => {
      const [controlledApps, guards, treatments] = await Promise.allSettled([
        utils.account.controlledAppAccess.ensureData(),
        utils.guard.getGuards.ensureData(MANAGED_GUARDS_INPUT),
        utils.split.check.ensureData({
          accountId,
          featureFlags: MANAGED_AUTH_SPLITS,
        }),
      ]);
      const valueOf = <T>(result: PromiseSettledResult<T>) =>
        result.status === "fulfilled" ? result.value : undefined;

      return resolveCreateBlock({
        isRestricted: buildIsAppRestricted(valueOf(controlledApps))(appId),
        isManagedCreateRestricted:
          isManagedAuthEnforcedBy(valueOf(treatments)) &&
          buildIsAppOperationRestricted(valueOf(guards), "create", {
            isOwnerOrSuperAdmin,
          })(appId),
        isManaged,
      });
    },
    [utils, accountId, isOwnerOrSuperAdmin],
  );
}
